import { ReactNode, useEffect, useRef, useState } from "react";

type LazyMountProps = {
  readonly children: ReactNode;
  readonly placeholder?: ReactNode;
  readonly rootMargin?: string;
  readonly className?: string;
};

/**
 * Renders its children only when its sentinel enters the viewport, then keeps
 * them mounted forever. Use to defer expensive subtrees (WebGL maps, SSE
 * streams) until the user scrolls them into view.
 */
export function LazyMount({
  children,
  placeholder = null,
  rootMargin = "200px",
  className,
}: LazyMountProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isVisible) return;
    const node = ref.current;
    if (!node) return;

    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [isVisible, rootMargin]);

  return (
    <div ref={ref} className={className}>
      {isVisible ? children : placeholder}
    </div>
  );
}
